// ... (imports remain unchanged)
import React, { useEffect, useState, useRef } from "react";

import "./PhraseGame.css";
import { FaPowerOff } from "react-icons/fa";

import hoverSound from "../../../assets/audio/sound-effect-hover.wav";
import startSound from "../../../assets/audio/sound-effect-new-question.wav";
import correctSound from "../../../assets/audio/sound-effect-correct.wav";
import incorrectSound from "../../../assets/audio/sound-effect-incorrect.wav";
import countdownSound from "../../../assets/audio/countdown-3-2-1.mp3";
import { getExercisesData } from "../../../firebase-functions/database";

// audio utilities
const hoverAudio = new Audio(hoverSound);
const startAudio = new Audio(startSound);
const correctAudio = new Audio(correctSound);
const incorrectAudio = new Audio(incorrectSound);

const playHoverSound = () => {
  hoverAudio.currentTime = 0;
  hoverAudio.volume = 0.05;
  hoverAudio.play();
};

const playStartSound = () => {
  startAudio.currentTime = 0;
  startAudio.volume = 0.05;
  startAudio.play();
};

const playCorrectSound = () => {
  correctAudio.currentTime = 0;
  correctAudio.volume = 0.1;
  correctAudio.play();
};

const playIncorrectSound = () => {
  incorrectAudio.currentTime = 0;
  incorrectAudio.volume = 0.1;
  incorrectAudio.play();
};

const scrambleWord = (word, setText, callback) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  let display = Array(word.length).fill(" ");
  let progress = 0;
  const interval = setInterval(() => {
    if (progress >= word.length) {
      clearInterval(interval);
      callback();
      return;
    }
    for (let i = progress; i < word.length; i++) {
      display[i] = chars[Math.floor(Math.random() * chars.length)];
    }
    display[progress] = word[progress];
    setText(display.join(""));
    progress++;
  }, 40);
};

export const PhraseGame = () => {
  const [toggleGameOn, setGameOn] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentTokenIndex, setCurrentTokenIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState();
  const [userAnswers, setUserAnswers] = useState();
  const [usedOptions, setUsedOptions] = useState([]);
  const [feedbackAnimation, setFeedbackAnimation] = useState("");
  const [displayedWords, setDisplayedWords] = useState([]);
  const [questionScore, setQuestionScore] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [tokenAttempts, setTokenAttempts] = useState([]);
  const [countdown, setCountdown] = useState(20);
  const [displayScore, setDisplayScore] = useState(0);
  const countdownInterval = useRef(null);
  const countdownAudioRef = useRef(null);
  const [wordAudio, setWordAudio] = useState(null);

  const shuffleArray = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  useEffect(() => {
    const totalScore = questionScore.reduce((acc, q) => acc + q.scoreEarned, 0);
    const interval = setInterval(() => {
      setDisplayScore((prev) => {
        if (prev < totalScore) return prev + 1;
        clearInterval(interval);
        return totalScore;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [questionScore]);

  useEffect(() => {
    if (!toggleGameOn) return;

    getExercisesData().then((res) => {
      if (!res.success) {
        console.error("Error fetching exercises data");
        return;
      }
      const rawQuestions = res.message.questions?.filter(
        (quest) => quest.exerciseId === "045ece1a-bb7b-413a-917f-b60b1cfe9c96"
      );

      //  const rawQuestions = questionBank["Book 1"]["Unit 1"]["Exercise 1"];
      const shuffled = shuffleArray(rawQuestions);
      setShuffledQuestions(shuffled);
      setCurrentQuestionIndex(0);
      setQuestionScore([]);
      playStartSound();
    });
  }, [toggleGameOn]);

  useEffect(() => {
    if (
      !shuffledQuestions.length ||
      currentQuestionIndex >= shuffledQuestions.length
    )
      return;

    const rawQuestion = shuffledQuestions[currentQuestionIndex];
    if (rawQuestion.audioUrl) {
      const newAudio = new Audio(rawQuestion.audioUrl);
      setWordAudio(newAudio);
    }

    const shuffledQuestion = {
      ...rawQuestion,
      tokens: rawQuestion.tokens.map((token) => ({
        ...token,
        options: shuffleArray(token.options),
      })),
    };

    setCurrentQuestion(shuffledQuestion);
    setUserAnswers(Array(shuffledQuestion.tokens.length).fill(""));
    setDisplayedWords(Array(shuffledQuestion.tokens.length).fill(""));
    setUsedOptions([]);
    setTokenAttempts(Array(shuffledQuestion.tokens.length).fill([]));
    setCurrentTokenIndex(0);
    setQuestionStartTime(new Date());
    setCountdown(20);

    if (countdownInterval.current) clearInterval(countdownInterval.current);
    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 5) {
          countdownAudioRef.current = new Audio(countdownSound);
          countdownAudioRef.current.volume = 0.2;
          setTimeout(() => countdownAudioRef.current.play(), 775);
        }
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    playStartSound();

    return () => {
      clearInterval(countdownInterval.current);
      if (countdownAudioRef.current) {
        countdownAudioRef.current.pause();
        countdownAudioRef.current.currentTime = 0;
      }
    };
  }, [currentQuestionIndex, shuffledQuestions]);

  useEffect(() => {
    if (countdown === 0) {
      setFeedbackAnimation("timeout-feedback");
      setTimeout(() => setFeedbackAnimation(""), 1000);
    }
  }, [countdown]);

  const handleChange = (index, value) => {
    if (!currentQuestion) return;

    const correct = currentQuestion.tokens[index].correct;

    setTokenAttempts((prev) => {
      const newAttempts = [...prev];
      newAttempts[index] = [...newAttempts[index], value];
      return newAttempts;
    });

    if (value !== correct) {
      setUsedOptions((prev) => [...prev, value]);
      playIncorrectSound();
      setFeedbackAnimation("incorrect-feedback");
      setTimeout(() => setFeedbackAnimation(""), 500);
      return;
    }

    const updatedAnswers = [...userAnswers];
    updatedAnswers[index] = value;
    setUserAnswers(updatedAnswers);

    scrambleWord(
      value,
      (text) => {
        const temp = [...displayedWords];
        temp[index] = text;
        setDisplayedWords(temp);
      },
      () => {
        if (index < currentQuestion.tokens.length - 1) {
          setTimeout(() => {
            setCurrentTokenIndex(index + 1);
          }, 300);
        } else {
          playCorrectSound();
          setFeedbackAnimation("correct-feedback");

          const timeCompleted = new Date();
          const timeTakenSeconds = Math.max(
            0,
            (timeCompleted - questionStartTime) / 1000 - 3
          );

          if (countdownAudioRef.current) {
            countdownAudioRef.current.pause();
            countdownAudioRef.current.currentTime = 0;
          }

          clearInterval(countdownInterval.current);

          const tokensData = currentQuestion.tokens.map((token, i) => {
            const attempts = tokenAttempts[i] || [];
            const correctOnTry =
              attempts.findIndex((a) => a === token.correct) + 1 ||
              attempts.length + 1;
            return {
              tokenIndex: i,
              correctAnswer: token.correct,
              attempts,
              correctOnTry,
            };
          });

          const tries = tokensData.map((t) => t.correctOnTry);
          let accuracyScore = 0;
          if (tries.every((t) => t === 1)) accuracyScore = 10;
          else if (tries.every((t) => t <= 2)) accuracyScore = 6;
          else accuracyScore = 3;

          let timeBonus = 0;
          if (timeTakenSeconds <= 5) timeBonus = 10;
          else if (timeTakenSeconds <= 20) {
            timeBonus = Math.max(
              1,
              10 - Math.floor((timeTakenSeconds - 5) / 1.5)
            );
          } else timeBonus = 0;

          const scoreEarned = accuracyScore + timeBonus;

          const result = {
            questionId: currentQuestion.id || `q-${currentQuestionIndex}`,
            timeStarted: questionStartTime.toISOString(),
            timeCompleted: timeCompleted.toISOString(),
            timeTakenSeconds,
            tokens: tokensData,
            accuracyScore,
            timeBonus,
            scoreEarned,
          };

          setQuestionScore((prev) => [...prev, result]);
          wordAudio.playbackRate = 0.8;
          wordAudio?.play();
          setTimeout(() => {
            setFeedbackAnimation("");
            setCurrentQuestionIndex((prev) => prev + 1);
          }, 3000);
        }
      }
    );
  };

  const isGameOver =
    toggleGameOn && currentQuestionIndex >= shuffledQuestions.length;
  const total = shuffledQuestions.length;
  const current = currentQuestionIndex;
  const progressPercent = Math.min((current / total) * 100, 100);
  const countdownPercent = (countdown / 20) * 100;
  const totalScore = questionScore.reduce((acc, q) => acc + q.scoreEarned, 0);

  return !toggleGameOn ? (
    <div className="game-load-screen-container">
      <div className="striped-overlay start-screen-inner">
        <button
          className="start-button"
          onClick={() => setGameOn(true)}
          onMouseEnter={playHoverSound}
        >
          <FaPowerOff className="icon" />
          Start Game
        </button>
      </div>
    </div>
  ) : isGameOver ? (
    <div className="game-end-screen striped-overlay">
      <h1>🎉 Great job!</h1>
      <p>You finished all the phrases. Come back tomorrow for more fun!</p>
      <h2>Your Final Score: {totalScore}</h2>
      <div className="end-score-summary">
        {questionScore.map((q, i) => (
          <p key={i}>
            <strong>Q{i + 1}:</strong> ⏱ {q.timeTakenSeconds.toFixed(1)}s | 🎯
            Accuracy: {q.accuracyScore} | ⏳ Bonus: {q.timeBonus} | 🧮 Total:{" "}
            {q.scoreEarned}
          </p>
        ))}
      </div>
    </div>
  ) : currentQuestion ? (
    <div
      className={`max-w-xl mx-auto p-4 border rounded shadow phrase-game-container ${feedbackAnimation}`}
    >
      {displayScore > 0 && (
        <div className="score-display-container">
          <div className="score-text striped-overlay">SCORE</div>
          <div className="score-value striped-overlay">{displayScore}</div>
        </div>
      )}

      {/* Progress Ring */}
      <div className="progress-ring-container striped-overlay">
        <svg className="progress-ring" width="120" height="120">
          <circle
            className="progress-ring__background"
            cx="60"
            cy="60"
            r="54"
          />
          <circle
            className="progress-ring__circle"
            cx="60"
            cy="60"
            r="54"
            strokeDasharray="339"
            strokeDashoffset={339 - (339 * progressPercent) / 100}
          />
        </svg>
        <div className="progress-ring__text">
          {current}/{total}
        </div>
      </div>

      {/* Countdown Ring */}
      <div
        className={`countdown-ring-container striped-overlay ${
          countdown === 0
            ? "disabled"
            : countdown === 1
            ? "expired"
            : countdown <= 3
            ? "warning"
            : ""
        }`}
      >
        <svg className="countdown-ring" width="120" height="120">
          <circle
            className="countdown-ring__background"
            cx="60"
            cy="60"
            r="54"
          />
          <circle
            className="countdown-ring__circle"
            cx="60"
            cy="60"
            r="54"
            strokeDasharray="339"
            strokeDashoffset={339 - (339 * countdownPercent) / 100}
          />
        </svg>
        <div className="countdown-ring__text">{countdown}</div>
      </div>

      {/* Image */}
      <div className="phrase-image-container" key={currentQuestionIndex}>
        <div className="striped-overlay">
          <img
            src={currentQuestion.imageUrl}
            alt="prompt"
            className="hologram-image"
          />
        </div>
      </div>

      {/* Option Buttons */}
      <div className="space-y-4 mb-4 phrase-options-container">
        <div
          className="flex flex-wrap gap-2 div-option-buttons"
          key={currentQuestionIndex + "-" + currentTokenIndex}
        >
          {currentQuestion?.tokens?.[currentTokenIndex]?.options.map(
            (option, i) => {
              const isUsed = usedOptions.includes(option);
              const isSelected = userAnswers[currentTokenIndex] === option;
              if (isUsed && !isSelected) return null;
              return (
                <button
                  key={i}
                  onMouseEnter={playHoverSound}
                  onClick={() => handleChange(currentTokenIndex, option)}
                  className={`holo-button ${
                    isSelected ? "bg-green-600 text-white" : ""
                  }`}
                >
                  {option}
                </button>
              );
            }
          )}
        </div>
      </div>

      {/* Collected Sentence */}
      <div className="collected-words-container striped-overlay">
        {displayedWords.map((word, i) => word || "...").join(" ")}
      </div>
    </div>
  ) : null;
};
